import { Route, Outlet, Navigate } from 'react-router-dom';
import { type RouteRegistryEntry } from 'FrontRoyalRouteRegistry/FrontRoyalRouteRegistry.types';
import { useCurrentUser } from 'FrontRoyalAngular';
import { ProtectedRoute } from 'ProtectedRoute';
import { hasGradingPortalAccess, hasGradingPortalAdminAccess } from './hasGradingPortalAccess';

const BASE = '/grading-portal';

export const GRADING_PORTAL_ROUTES = {
    GRADING_PORTAL: {
        path: BASE,
        ALL_SUBMISSIONS: { path: `${BASE}/all-submissions`, relativePath: 'all-submissions' },
        ASSIGN_GRADERS: { path: `${BASE}/assign-graders`, relativePath: 'assign-graders' },
        NEEDS_REVIEW: { path: `${BASE}/needs-review`, relativePath: 'needs-review' },
        S_AND_O_REVIEW: { path: `${BASE}/s-and-o-review`, relativePath: 's-and-o-review' },
        MY_GRADING_TASKS: { path: `${BASE}/my-grading-tasks`, relativePath: 'my-grading-tasks' },
    },
};

export function configureGradingPortalRoutes(): RouteRegistryEntry[] {
    return [
        {
            routes: GRADING_PORTAL_ROUTES,
            config: {},
        },
    ];
}

/**
 * Component that redirects to the appropriate default route based on user role.
 * Admins go to All Submissions, graders go to My Grading Tasks.
 */
function GradingPortalIndexRedirect() {
    const currentUser = useCurrentUser();
    const isAdmin = hasGradingPortalAdminAccess(currentUser);

    return (
        <Navigate
            to={
                isAdmin
                    ? GRADING_PORTAL_ROUTES.GRADING_PORTAL.ALL_SUBMISSIONS.path
                    : GRADING_PORTAL_ROUTES.GRADING_PORTAL.MY_GRADING_TASKS.path
            }
            replace
        />
    );
}

/**
 * Wrapper for admin-only routes. Redirects non-admins to My Grading Tasks.
 */
function AdminOnlyRoute({ children }: { children: React.ReactNode }) {
    const currentUser = useCurrentUser();
    const isAdmin = hasGradingPortalAdminAccess(currentUser);

    if (!isAdmin) {
        return <Navigate to={GRADING_PORTAL_ROUTES.GRADING_PORTAL.MY_GRADING_TASKS.path} replace />;
    }

    return <>{children}</>;
}

async function getLazyGradingPortalComponent(
    component: 'Layout' | 'AllSubmissions' | 'AssignGraders' | 'NeedsReview' | 'SAndOReview' | 'MyGradingTasks',
) {
    const components = await import(/* webpackPrefetch: true */ './GradingPortal');
    const GradingPortalComponent = components[component];
    if (component === 'Layout') {
        return {
            Component: () => (
                <ProtectedRoute
                    accessPolicy="custom"
                    accessPolicyFn={({ currentUser }) => hasGradingPortalAccess(currentUser)}
                >
                    <GradingPortalComponent />
                </ProtectedRoute>
            ),
        };
    }
    // Wrap admin-only routes
    if (
        component === 'AllSubmissions' ||
        component === 'AssignGraders' ||
        component === 'NeedsReview' ||
        component === 'SAndOReview'
    ) {
        return {
            Component: () => (
                <AdminOnlyRoute>
                    <GradingPortalComponent />
                </AdminOnlyRoute>
            ),
        };
    }
    return { Component: GradingPortalComponent };
}

export function getGradingPortalRoutes() {
    return (
        <Route id="grading-portal-routes" element={<Outlet />}>
            <Route
                path={GRADING_PORTAL_ROUTES.GRADING_PORTAL.path}
                lazy={() => getLazyGradingPortalComponent('Layout')}
            >
                <Route index element={<GradingPortalIndexRedirect />} />
                <Route
                    path={GRADING_PORTAL_ROUTES.GRADING_PORTAL.ALL_SUBMISSIONS.relativePath}
                    lazy={() => getLazyGradingPortalComponent('AllSubmissions')}
                />
                <Route
                    path={GRADING_PORTAL_ROUTES.GRADING_PORTAL.ASSIGN_GRADERS.relativePath}
                    lazy={() => getLazyGradingPortalComponent('AssignGraders')}
                />
                <Route
                    path={GRADING_PORTAL_ROUTES.GRADING_PORTAL.NEEDS_REVIEW.relativePath}
                    lazy={() => getLazyGradingPortalComponent('NeedsReview')}
                />
                <Route
                    path={GRADING_PORTAL_ROUTES.GRADING_PORTAL.S_AND_O_REVIEW.relativePath}
                    lazy={() => getLazyGradingPortalComponent('SAndOReview')}
                />
                <Route
                    path={GRADING_PORTAL_ROUTES.GRADING_PORTAL.MY_GRADING_TASKS.relativePath}
                    lazy={() => getLazyGradingPortalComponent('MyGradingTasks')}
                />
            </Route>
        </Route>
    );
}
